import ExpandLess from '@mui/icons-material/ExpandLess'
import ExpandMore from '@mui/icons-material/ExpandMore'
import Box from '@mui/material/Box'
import Collapse from '@mui/material/Collapse'
import Drawer from '@mui/material/Drawer'
import ListItemButton from '@mui/material/ListItemButton'
import Stack from '@mui/material/Stack'
import { alpha } from '@mui/material/styles'
import { useEffect, useState } from 'react'
import { NavLink, useLocation } from 'react-router-dom'
import Logo from '../components/logo'
import { useAuth } from '../context/AuthProvider'
import { useResponsive } from '../hooks/use-responsive'
import { Permission } from '../types/User'
import { NAV } from './config-layout'
import navConfig, { MenuConfig, MenuType, platformNavConfig } from './config-navigation'
import { isPlatformPath } from '../utils/platformMode'

interface NavProps {
  openNav: boolean
  onCloseNav: () => void
}

export default function Nav({ openNav, onCloseNav }: NavProps) {
  const { pathname } = useLocation()
  const upLg = useResponsive('up', 'lg')
  const { userHasPermission } = useAuth()

  useEffect(() => {
    if (openNav) {
      onCloseNav()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname])
 

  const canShow = (required?: Permission | Permission[]) =>
    !required || userHasPermission(required)
  
  // The platform area has its own menu; the client menu is never mixed into it.
  const activeNavConfig = isPlatformPath(pathname) ? platformNavConfig : navConfig

  const filteredNavConfig = activeNavConfig
    .map((item) => {
      if (item.type === MenuType.Submenu) {
        if (!canShow(item.allowedPermission)) return null
  
        const children = item.children.filter((child) =>
          canShow(child.allowedPermission)
        )
  
        return children.length ? { ...item, children } : null
      }
  
      return canShow(item.allowedPermission) ? item : null
    })
    .filter(Boolean) as MenuConfig[]
    
    const renderContent = (
      <>
        <Logo sx={{ mt: 3, ml: 4, mb: 4 }} />
    
        <Stack component="nav" spacing={0.5} sx={{ px: 2 }}>
          {filteredNavConfig.map((item) => (
            <NavItem key={item.title} item={item} />
          ))}
        </Stack>
      </>
    )

  return (
    <Box
    id="nav-box"
      sx={{
        flexShrink: { lg: 0 },
        width: { lg: NAV.WIDTH },
      }}
    >
      {upLg ? (
        <Box
        id="nav-box-lg"
        sx={{
          position: 'fixed',
          width: NAV.WIDTH,
          height: '100vh',
          overflowY: 'auto',
          borderRight: (theme) => `dashed 1px ${theme.palette.divider}`,
        }}
      >
        {renderContent}
      </Box>
      ) : (
        <Drawer
          open={openNav}
          onClose={onCloseNav}
          slotProps={{
            paper: {
              sx: {
                width: NAV.WIDTH,
              },
            }
          }}
        >
          {renderContent}
        </Drawer>
      )}
    </Box>
  );
}

// ----------------------------------------------------------------------

interface NavItemProps {
  item: MenuConfig
}

function NavItem({ item }: NavItemProps) {
  const { pathname } = useLocation()
  const [open, setOpen] = useState(false)

  useEffect(() => {
    if (item.type === MenuType.Submenu) {
      const isAnyChildActive = item.children.some(
        (child) => child.path === pathname
      )
      if (isAnyChildActive) {
        setOpen(true)
      }
    }
  }, [item, pathname])

  const handleClick = () => {
    setOpen(!open)
  }

  if (item.type === MenuType.Item) {
    const active = item.path === pathname

    return (
      <ListItemButton
        component={NavLink}
        to={item.path}
        sx={{
          minHeight: 44,
          borderRadius: 0.75,
          typography: 'body2',
          color: 'text.secondary',
          textTransform: 'capitalize',
          fontWeight: 'fontWeightMedium',
          ...(active && {
            color: 'primary.main',
            fontWeight: 'fontWeightSemiBold',
            bgcolor: (theme) => alpha(theme.palette.primary.main, 0.08),
            '&:hover': {
              bgcolor: (theme) => alpha(theme.palette.primary.main, 0.16),
            },
          }),
        }}
      >
        <Box component="span" sx={{ width: 24, height: 24, mr: 2 }}>
          {item.icon}
        </Box>

        <Box component="span" sx={{ flexGrow: 1 }}>
          {item.title}
        </Box>
      </ListItemButton>
    )
  }

  if (item.type === MenuType.Submenu) {
    return (
      <>
        <ListItemButton
          onClick={handleClick}
          sx={{
            minHeight: 44,
            borderRadius: 0.75,
            typography: 'body2',
            color: 'text.secondary',
            textTransform: 'capitalize',
            fontWeight: 'fontWeightMedium',
          }}
        >
          <Box component="span" sx={{ width: 24, height: 24, mr: 2 }}>
            {item.icon}
          </Box>

          <Box component="span" sx={{ flexGrow: 1 }}>
            {item.title}
          </Box>

          {open ? <ExpandLess /> : <ExpandMore />}
        </ListItemButton>

        <Collapse in={open} timeout="auto" unmountOnExit>
          <Stack component="nav" spacing={0.5} sx={{ pl: 4 }}>
            {item.children.map((child) => (
              <NavItem key={child.title} item={child} />
            ))}
          </Stack>
        </Collapse>
      </>
    )
  }

  return null
}